/**
 * Password field with a show/hide toggle, mirroring the eye control mobile
 * renders in `common/AppInput`.
 *
 * The caller keeps ownership of the input: every native prop (including the
 * page's own `className`, so this works with both `auth-flow__input` and
 * `settings-input`) is forwarded untouched. Only `type` is managed here.
 */
import { useState, type InputHTMLAttributes } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { EyeIcon, EyeOffIcon } from './icons';
import './passwordInput.css';

export type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

export function PasswordInput({ className = '', ...rest }: PasswordInputProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <span className="password-input">
      <input
        {...rest}
        type={visible ? 'text' : 'password'}
        className={`password-input__field ${className}`.trim()}
      />
      <button
        type="button"
        className="password-input__toggle"
        // These fields sit inside a <label>, which forwards clicks to its
        // control; without stopPropagation the label would re-dispatch this
        // one. preventDefault on mousedown keeps the caret in the field
        // instead of blurring it to the button.
        onMouseDown={event => event.preventDefault()}
        onClick={event => {
          event.stopPropagation();
          setVisible(current => !current);
        }}
        aria-label={t(
          visible ? 'common.accessibility.hidePassword' : 'common.accessibility.showPassword',
        )}
        aria-pressed={visible}
      >
        {visible ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
      </button>
    </span>
  );
}
